// =============================================================
// Auth Context
// Provides authentication state and actions globally.
// =============================================================

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authApi, AuthUser, MeResponse, OrgSummary, ApiError } from '../lib/api';

interface AuthState {
  user: MeResponse | null;
  organization: OrgSummary | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName: string;
  }) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    organization: null,
    token: localStorage.getItem('auth_token'),
    isLoading: true,
    isAuthenticated: false,
  });

  // Restore session on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setState((s) => ({ ...s, isLoading: false }));
      return;
    }

    authApi.me()
      .then((me) => {
        const primaryMembership = me.memberships[0];
        setState({
          user: me,
          token,
          organization: primaryMembership
            ? {
                id: primaryMembership.organizationId,
                name: primaryMembership.organizationName,
                slug: primaryMembership.organizationSlug,
              }
            : null,
          isLoading: false,
          isAuthenticated: true,
        });
      })
      .catch(() => {
        localStorage.removeItem('auth_token');
        setState({ user: null, organization: null, token: null, isLoading: false, isAuthenticated: false });
      });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await authApi.login({ email, password });
    localStorage.setItem('auth_token', result.token);

    // Fetch full profile after login
    const me = await authApi.me();
    setState({
      user: me,
      token: result.token,
      organization: result.organization,
      isLoading: false,
      isAuthenticated: true,
    });
  }, []);

  const register = useCallback(async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName: string;
  }) => {
    const result = await authApi.register(data);
    localStorage.setItem('auth_token', result.token);

    const me = await authApi.me();
    setState({
      user: me,
      token: result.token,
      organization: result.organization,
      isLoading: false,
      isAuthenticated: true,
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    setState({
      user: null,
      organization: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,
    });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
